import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';
import { 
  FileText, Upload, Search, Download, Trash2, 
  Eye, RefreshCw, CheckCircle, Clock, XCircle,
  FileOutput, Users, FileBox, Truck, Receipt, File,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import DocumentVerifyModal from './DocumentVerifyModal';

// Tipus
interface Document {
  id: string;
  tenantId: string;
  categoryId?: number;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  storagePath: string;
  ocrStatus: 'pending' | 'processing' | 'completed' | 'failed';
  ocrText?: string;
  ocrConfidence?: number;
  ocrProcessedAt?: string;
  metadata?: Record<string, { value: string; confidence: number }>;
  validationStatus?: 'pending' | 'verified' | 'rejected';
  validatedBy?: string;
  validatedAt?: string;
  uploadedBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  id: number;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  isActive: boolean;
  sortOrder: number;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'facturas_compra': <FileText size={16} />,
  'facturas_venta': <FileOutput size={16} />,
  'nominas': <Users size={16} />,
  'contratos': <FileBox size={16} />,
  'albaranes': <Truck size={16} />,
  'recibos': <Receipt size={16} />,
  'otros': <File size={16} />,
};

const OCR_STATUS_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600 bg-yellow-100', label: 'Pendent' },
  processing: { icon: RefreshCw, color: 'text-blue-600 bg-blue-100 animate-spin', label: 'Processant' },
  completed: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Completat' },
  failed: { icon: XCircle, color: 'text-red-600 bg-red-100', label: 'Error' },
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [ocrStatusFilter, setOcrStatusFilter] = useState<string>('');
  const [validationFilter, setValidationFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [verifyingDoc, setVerifyingDoc] = useState<Document | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: Category[] }>('/ocr-documents/categories');
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Carregar documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (selectedCategory) params.set('categoryId', selectedCategory);
      if (searchTerm) params.set('search', searchTerm);
      if (ocrStatusFilter) params.set('ocrStatus', ocrStatusFilter);
      if (validationFilter) params.set('validationStatus', validationFilter);

      const response = await api.get<{ 
        success: boolean; 
        data: Document[]; 
        total: number; 
        page: number; 
        totalPages: number 
      }>(`/ocr-documents?${params.toString()}`);

      if (response.success) {
        setDocuments(response.data);
        setTotal(response.total);
        setTotalPages(response.totalPages);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, searchTerm, ocrStatusFilter, validationFilter]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Auto-refresh quan hi ha documents processant
  useEffect(() => {
    const hasProcessing = documents.some(d => d.ocrStatus === 'processing');
    if (hasProcessing) {
      const interval = setInterval(() => {
        fetchDocuments();
      }, 3000); // Refrescar cada 3 segons
      return () => clearInterval(interval);
    }
  }, [documents, fetchDocuments]);

  // Upload de fitxers
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      if (selectedCategory) {
        formData.append('categoryId', selectedCategory);
      }

      await apiClient.post('/ocr-documents/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Drag & Drop
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      if (selectedCategory) {
        formData.append('categoryId', selectedCategory);
      }

      await apiClient.post('/ocr-documents/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
    }
  };

  // Eliminar document
  const handleDelete = async (id: string) => {
    if (!confirm('Estàs segur que vols eliminar aquest document?')) return;
    try {
      await api.delete(`/ocr-documents/${id}`);
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Reprocessar OCR
  const handleReprocess = async (id: string) => {
    try {
      await api.post(`/ocr-documents/${id}/reprocess`);
      await fetchDocuments();
    } catch (error) {
      console.error('Error reprocessing document:', error);
    }
  };

  const openDocBlob = async (endpoint: string) => {
    try {
      const blob = await api.getBlob(endpoint);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Error obrint document:', err);
    }
  };

  // Descarregar documents seleccionats (un per un)
  const handleDownloadSelected = async () => {
    if (selectedDocs.size === 0) return;
    selectedDocs.forEach(id => {
      const doc = documents.find(d => d.id === id);
      if (doc?.storagePath) {
        openDocBlob(`/ocr-documents/${id}/file`);
      }
    });
  };

  // Seleccionar/Deseleccionar tots
  const toggleSelectAll = () => {
    if (selectedDocs.size === documents.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(documents.map(d => d.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDocs(newSelected);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

const getCategoryName = (categoryId?: number): string => {
  if (!categoryId) return 'Sense categoria';
  const cat = categories.find(c => c.id === categoryId);
  return cat?.name || 'Desconegut';
  };

const getCategoryIcon = (categoryId?: number): React.ReactNode => {
  if (!categoryId) return <File size={16} />;
  const cat = categories.find(c => c.id === categoryId);
  return CATEGORY_ICONS[cat?.code || ''] || <File size={16} />;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Capcalera */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
        <p className="text-muted-foreground">
          Gestió documental amb OCR intel·ligent
        </p>
      </div>

      {/* Botó pujar documents */}
      <div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'Pujant...' : 'Pujar documents'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.gif,.tiff"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={18} className="text-slate-400" />
            <input
              type="text"
              placeholder="Cercar documents..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="flex-1 border-0 focus:ring-0 text-sm"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm whitespace-nowrap"
          >
            <option value="">Totes les categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <ExcelFilterDropdown
            label="Estat OCR"
            options={[
              { value: 'pending', label: 'Pendent' },
              { value: 'processing', label: 'Processant' },
              { value: 'completed', label: 'Completat' },
              { value: 'failed', label: 'Error' },
            ]}
            selected={ocrStatusFilter ? ocrStatusFilter.split(',') : []}
            onApply={(values) => { setOcrStatusFilter(values.join(',')); setPage(1); }}
          />

          <ExcelFilterDropdown
            label="Validació"
            options={[
              { value: 'pending', label: 'Pendent' },
              { value: 'verified', label: 'Verificat' },
              { value: 'rejected', label: 'Rebutjat' },
            ]}
            selected={validationFilter ? validationFilter.split(',') : []}
            onApply={(values) => { setValidationFilter(values.join(',')); setPage(1); }}
          />

          <button
            onClick={() => { 
              setSearchTerm(''); 
              setSelectedCategory(''); 
              setOcrStatusFilter(''); 
              setValidationFilter('');
              setPage(1);
            }}
            className="text-sm text-slate-500 hover:text-slate-700 whitespace-nowrap"
          >
            Netejar filtres
          </button>
        </div>
      </div>

      {/* Zona drag & drop */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
      >
        <Upload size={32} className="mx-auto text-slate-400 mb-2" />
        <p className="text-slate-600">Arrossega fitxers aquí o fes clic a &quot;Pujar documents&quot;</p>
        <p className="text-sm text-slate-400 mt-1">PDF, JPG, PNG fins a 50MB</p>
      </div>

      {/* Taula de documents */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDocs.size === documents.length && documents.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300"
                  />
                </th>
<th className="px-4 py-3 text-left font-medium text-slate-600">Document</th>
<th className="px-4 py-3 text-left font-medium text-slate-600">Proveïdor</th>
<th className="px-4 py-3 text-left font-medium text-slate-600">Categoria</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Mida</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">OCR</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Validació</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Data</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    Carregant...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                    No hi ha documents
                  </td>
                </tr>
              ) : (
                documents.map((doc) => {
                  const ocrConfig = OCR_STATUS_CONFIG[doc.ocrStatus];
                  const OcrIcon = ocrConfig.icon;
                  
                  return (
                    <tr key={doc.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedDocs.has(doc.id)}
                          onChange={() => toggleSelect(doc.id)}
                          className="rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-3">
<div className="flex items-center gap-3">
  <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
  {doc.mimeType === 'application/pdf' ? (
  <FileText size={20} className="text-red-500" />
  ) : (
                              <Eye size={20} className="text-blue-500" />
                            )}
                          </div>
<div>
  <p className="font-medium text-slate-800 truncate max-w-[200px]">
  {doc.originalFilename}
  </p>
  </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="truncate max-w-[150px] block">
                          {(doc.metadata?.empresa_emisora?.value as string) || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          {getCategoryIcon(doc.categoryId)}
                          <span>{getCategoryName(doc.categoryId)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatFileSize(doc.fileSize)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${ocrConfig.color}`}>
                          <OcrIcon size={12} />
                          {ocrConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {doc.validationStatus === 'verified' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                            <CheckCircle size={12} />
                            Verificat
                          </span>
                        ) : doc.validationStatus === 'pending' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                            <Clock size={12} />
                            Per verificar
                          </span>
                        ) : doc.validationStatus === 'rejected' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                            <XCircle size={12} />
                            Rebutjat
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(doc.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDocBlob(`/ocr-documents/${doc.id}/view`)}
                            className="p-1 text-slate-400 hover:text-blue-600"
                            title="Veure"
                          >
                            <Eye size={18} />
                          </button>
                          {doc.ocrStatus === 'completed' && doc.validationStatus !== 'verified' && (
                            <button
                              onClick={() => setVerifyingDoc(doc)}
                              className="p-1 text-slate-400 hover:text-green-600"
                              title="Verificar"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          {(doc.ocrStatus === 'failed' || doc.ocrStatus === 'completed') && (
                            <button
                              onClick={() => handleReprocess(doc.id)}
                              className="p-1 text-slate-400 hover:text-orange-600"
                              title="Reprocessar OCR"
                            >
                              <RefreshCw size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="p-1 text-slate-400 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginació */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <p className="text-sm text-slate-600">
              Mostrant {documents.length} de {total} documents
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-slate-600">
                Pàgina {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-50"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de verificació */}
      {verifyingDoc && (
        <DocumentVerifyModal
          document={verifyingDoc}
          onClose={() => {
            setVerifyingDoc(null);
            fetchDocuments();
          }}
          onSaveField={async (docId, field, value) => {
            await api.patch(`/ocr-documents/${docId}/metadata`, { fieldName: field, value });
          }}
          onVerify={async (docId) => {
            await api.post(`/ocr-documents/${docId}/verify`);
          }}
        />
      )}
    </div>
  );
}
